/**
 * The big instruction sign shown while navigating.
 *
 * Layout, top to bottom:
 *   ┌───────────────────────────────────────────┐
 *   │  ON  [304]              →  [290]          │  road strip: where you are,
 *   ├───────────────────────────────────────────┤  where you're going next
 *   │  ⤴   400 m                        then ↰  │
 *   │      Route 290                            │
 *   ├───────────────────────────────────────────┤
 *   │  ⚠ DO NOT STAY IN THE LEFT LANE           │  only when it's a trap
 *   ├───────────────────────────────────────────┤
 *   │  [↰][↑][↑ ↱][↱]                           │  lane bar
 *   └───────────────────────────────────────────┘
 *
 * Two requirements drive this shape:
 *
 *  - "I always want to see which road number we are on and which is the next
 *    road we turn onto." Hence the permanent road strip: it does not wait for
 *    a maneuver, it is always showing the current route number.
 *  - "No signs can ever be in Thai if English is selected - rather just road
 *    number." Every string here goes through the script guard in nav/roads.ts,
 *    which substitutes the shield number when the OSM name is unreadable.
 *
 * Imminence model: inside a speed-scaled threshold the card grows, the accent
 * animates and the arrow pulses. The lane bar flashes only when a lane change
 * is genuinely required.
 */

import type { NavProgress } from '../core/types';
import type { UnitSystem } from '../core/settings';
import { formatDistance } from '../core/format';
import { arrowSvg, glyphForManeuver, laneChipSvg } from './arrows';
import { getLang, t } from '../core/i18n';
import { primaryRef, roadStripLabel, roadStripParts, safeBannerText, stepRefs } from '../nav/roads';
import { tierForStep, type RoadTier } from '../nav/paceModel';
import { uturnSign } from '../nav/turnSide';
import { forkSentence, laneSentence } from '../nav/phrasing';

export class ManeuverCard {
  readonly el: HTMLElement;
  private lastRenderKey = '';
  /** Village / sub-district we are in, shown when the road itself is anonymous. */
  private locality: string | null = null;

  setLocality(name: string | null): void {
    if (name === this.locality) return;
    this.locality = name;
    this.lastRenderKey = ''; // force a redraw with the new context
  }

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'maneuver-card';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  hide(): void {
    this.el.hidden = true;
    this.lastRenderKey = '';
  }

  update(progress: NavProgress, units: UnitSystem, speedMs: number): void {
    const lang = getLang();
    const { step, maneuverStep, banner, lanes, laneAdvice, distanceToManeuver, drivingSide } =
      progress;

    // Imminent when within ~12 s of travel or 80 m, whichever is larger.
    const imminentAt = Math.max(80, speedMs * 12);
    const imminent = distanceToManeuver <= imminentAt;

    const m = maneuverStep?.maneuver;
    const modifier = banner?.primary.modifier ?? m?.modifier;
    // For a U-turn, measure which way the route really swings rather than
    // inferring it from the driving side - see nav/turnSide.ts.
    const swing =
      (modifier ?? '').toLowerCase() === 'uturn' ? uturnSign(step, maneuverStep) : 0;
    const glyph = glyphForManeuver(
      banner?.primary.type ?? m?.type ?? 'continue',
      modifier,
      drivingSide,
      swing,
    );

    // Road strip: the number under our wheels, and the number we join. Falls
    // back to a road-class description so the strip is never blank on the many
    // Bangkok streets that have a Thai-only name and no route number.
    const on = roadStripParts(step, lang);
    // A bare road class ("Street") says nothing about where you are, so pair
    // it with the village name when we have one.
    const onRoad =
      on.generic && this.locality ? `${on.label} · ${this.locality}` : on.label;
    const toRoad = roadStripLabel(maneuverStep, lang);
    const onRef = primaryRef(step);
    const toRef = primaryRef(maneuverStep);
    const onTier = step ? tierForStep(step) : 'single';
    const toTier = maneuverStep ? tierForStep(maneuverStep) : 'single';

    // Headline: prefer readable banner text, else the road we're joining.
    const primaryText = safeBannerText(banner, maneuverStep, lang) || toRoad;
    const secondaryText = exitLabel(maneuverStep, lang);
    const distText = formatDistance(distanceToManeuver, units);

    const next = progress.nextStep;
    const thenGlyph =
      next && distanceToManeuver < 500
        ? glyphForManeuver(next.maneuver.type, next.maneuver.modifier, drivingSide)
        : null;

    const trap = laneAdvice?.trap ?? null;
    const laneWarning = trap ? laneSentence(laneAdvice, lang) : '';
    const laneChangeNeeded = laneAdvice?.changeRequired === true;

    // The branch ahead. Always drawn while in range, whatever the countdown is
    // pointing at - the split in the carriageway is a decision the driver makes
    // long before the turn 8 km later. See nav/forks.ts.
    const fork = progress.fork;
    const forkText = forkSentence(fork, lang);
    const forkDist = fork ? formatDistance(fork.distance, units) : '';

    // Cheap dirty-check so we only touch the DOM when content changes;
    // distance ticks every fix but the rest is stable per maneuver.
    const key = [
      glyph,
      primaryText,
      secondaryText,
      distText,
      imminent,
      thenGlyph,
      onRoad,
      toRoad,
      glyph,
      onRef ?? '',
      toRef ?? '',
      this.locality ?? '',
      laneWarning,
      lanes ? lanes.map((l) => `${l.valid}:${l.indications.join('|')}`).join(',') : '',
      forkText,
      forkDist,
    ].join('~');
    if (key === this.lastRenderKey) return;
    this.lastRenderKey = key;

    this.el.hidden = false;
    this.el.classList.toggle('is-imminent', imminent);
    this.el.classList.toggle('has-lane-change', laneChangeNeeded && imminent);
    this.el.classList.toggle('has-lane-trap', !!trap);

    const fallbackIndication = laneIndicationFor(m?.modifier);

    this.el.innerHTML = `
      ${
        onRoad || toRoad
          ? `<div class="road-strip">
               <span class="road-strip__now">
                 <span class="road-strip__label">${t('sign.on')}</span>
                 ${shield(onRoad, onRef, onTier)}
               </span>
               ${
                 toRoad && toRoad !== onRoad
                   ? `<span class="road-strip__arrow">${arrowSvg('right', 'road-strip__arrow-glyph')}</span>
                      <span class="road-strip__next">
                        <span class="road-strip__label">${t('sign.then')}</span>
                        ${shield(toRoad, toRef, toTier)}
                      </span>`
                   : ''
               }
             </div>`
          : ''
      }
      <div class="maneuver-card__main">
        <div class="maneuver-card__arrow">${arrowSvg(glyph, 'maneuver-card__glyph')}</div>
        <div class="maneuver-card__text">
          <div class="maneuver-card__distance">${distText}</div>
          ${primaryText ? `<div class="maneuver-card__primary">${escapeHtml(primaryText)}</div>` : ''}
          ${secondaryText ? `<div class="maneuver-card__secondary">${escapeHtml(secondaryText)}</div>` : ''}
        </div>
        ${
          thenGlyph
            ? `<div class="maneuver-card__then"><span>${t('nav.then')}</span>${arrowSvg(thenGlyph, 'maneuver-card__then-glyph')}</div>`
            : ''
        }
      </div>
      ${
        forkText
          ? `<div class="fork-strip fork-strip--${fork!.side}">
               <span class="fork-strip__glyph">${arrowSvg(
                 glyphForManeuver(
                   fork!.step.maneuver.type,
                   fork!.step.maneuver.modifier,
                   drivingSide,
                 ),
                 'fork-strip__icon',
               )}</span>
               <span class="fork-strip__text">${escapeHtml(forkText)}</span>
               <span class="fork-strip__dist">${escapeHtml(forkDist)}</span>
             </div>`
          : ''
      }
      ${
        laneWarning
          ? `<div class="lane-warning" role="alert">
               <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 L22 20 H2 Z" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"/><path d="M12 10 V14 M12 16.6 V17" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
               <span>${escapeHtml(laneWarning)}</span>
             </div>`
          : ''
      }
      ${
        lanes?.length
          ? `<div class="lane-bar" role="img" aria-label="${escapeHtml(laneAria(progress, lang))}">
               <div class="lane-sign">${lanes
                 .map((l) =>
                   laneChipSvg(l.indications, l.valid_indication, l.valid, drivingSide, fallbackIndication),
                 )
                 .join('')}</div>
             </div>`
          : ''
      }
    `;
  }
}

/**
 * Route-number badge styled like the marker actually planted beside the road.
 *
 * Thai national highways are a white plate with a dark border and black
 * numerals; the motorways (7 and 9) are a blue plate with white numerals. The
 * driver reads these at 100 km/h on the roadside, so matching them means the
 * sign in the app and the sign out of the window agree at a glance.
 *
 * The name is kept alongside the number rather than replaced by it: the number
 * says which road, the name says where you are on it.
 */
function shield(label: string, ref: string | null, tier: RoadTier): string {
  if (!label) return '';
  if (!ref) return `<span class="road-strip__name">${escapeHtml(label)}</span>`;

  const kind = tier === 'motorway' ? 'motorway' : 'highway';
  const badge = `<span class="shield shield--${kind} ${
    ref.length > 3 ? 'shield--wide' : ''
  }">${escapeHtml(ref)}</span>`;

  // Only append the name when it says something the number does not.
  const rest = label.replace(ref, '').replace(/^[\s/·-]+/, '').trim();
  return rest
    ? `${badge}<span class="road-strip__name">${escapeHtml(rest)}</span>`
    : badge;
}

/** Exit number / destination line, script-guarded. */
function exitLabel(step: NavProgress['maneuverStep'], lang: 'en' | 'th'): string {
  if (!step) return '';
  const exits = (step.exits ?? '').trim();
  if (exits) return lang === 'th' ? `ทางออก ${exits}` : `Exit ${exits}`;
  const refs = stepRefs(step);
  return refs.length > 1 ? refs.slice(1).join(' / ') : '';
}

/** Screen-reader description of the lane bar. */
function laneAria(progress: NavProgress, lang: 'en' | 'th'): string {
  const spoken = laneSentence(progress.laneAdvice, lang);
  return spoken || (lang === 'th' ? 'ช่องทางเดินรถ' : 'Lane guidance');
}

/** The lane indication implied by the maneuver, used when Mapbox omits one. */
function laneIndicationFor(modifier?: string): import('../core/types').LaneIndication | undefined {
  const m = (modifier ?? '').toLowerCase();
  switch (m) {
    case 'left':
    case 'right':
    case 'slight left':
    case 'slight right':
    case 'sharp left':
    case 'sharp right':
    case 'uturn':
    case 'straight':
      return m as import('../core/types').LaneIndication;
    default:
      return undefined;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
